'use client';

/**
 * <CourseThumbnail>
 *
 * Single source of truth for rendering a course thumbnail across the FE.
 *
 * Why this exists (tickets 099475cd + 1af309c2, 2026-05-14):
 *   - `courses.thumbnail_url IS NULL` for some seeded rows (e.g. Global
 *     "Phishing Awareness & Email Security"). BE response then carries
 *     `thumbnailUrl: ""`.
 *   - Different renderers handled the empty case inconsistently:
 *       - Edit page: fell back to `/thumbnails/1.svg` (loads).
 *       - Portal trainings card: rendered `<img src="">` (broken, leaked
 *         alt text under the gradient).
 *       - Course list table: rendered `<img src="">` (broken icon).
 *   - This component renders a deterministic letter-avatar placeholder when
 *     `src` is empty/null and falls back to the same placeholder on `onError`.
 *     Result: no view ever ships `<img src="">` again.
 *
 * Contract:
 *   src        — absolute URL, relative path, or empty/null (no thumbnail set).
 *                Relative paths are prefixed via `resolveAssetUrl`.
 *   courseName — used for alt-text + first-letter avatar fallback.
 *   className  — applied to the outer wrapper (controls size + radius).
 *
 * Layout: the wrapper is the size-controlling box. The <img> fills it
 * (`object-cover`). The fallback overlays the same box and shows the
 * first letter of courseName on a gradient background derived deterministically
 * from the course name. Never renders `<img src="">`.
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import { BookOpen } from 'lucide-react';
import { resolveAssetUrl } from '@/lib/utils/resolve-asset-url';

/**
 * Canonical LMS default course thumbnail. Mirrors
 * `CoursesService.DEFAULT_THUMBNAIL_URL` on the BE (commit f751ccbb).
 * Used as a Tier-2 fallback when the per-course `src` is empty/null —
 * legacy courses created before the BE default-on-create rule shipped
 * have `thumbnail_url IS NULL`, and this surface is what unifies them
 * back to the canonical PNG asset on the FE side. Wired 2026-05-14
 * per portal triage product feedback.
 */
const DEFAULT_THUMBNAIL_URL = '/lms/thumbnails/default-course.png';

interface CourseThumbnailProps {
  src?: string | null;
  courseName: string;
  className?: string;
  /** Optional override for the alt attribute. Defaults to `courseName`. */
  alt?: string;
  /** Render with a fixed aspect ratio (used by gallery + carousel renderers). */
  showIcon?: boolean;
}

/**
 * Deterministic first letter from courseName (skips whitespace / symbols).
 * Falls back to "?" when nothing usable.
 */
function firstLetter(courseName: string): string {
  const m = courseName.match(/[A-Za-z0-9]/);
  return m ? m[0].toUpperCase() : '?';
}

/**
 * Pick a deterministic gradient class from the courseName hash so the
 * placeholder looks like a designed asset, not a debug placeholder.
 */
const GRADIENTS = [
  'from-blue-500/30 to-indigo-500/10',
  'from-emerald-500/30 to-teal-500/10',
  'from-amber-500/30 to-orange-500/10',
  'from-rose-500/30 to-pink-500/10',
  'from-violet-500/30 to-fuchsia-500/10',
  'from-cyan-500/30 to-sky-500/10',
];

function gradientFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
}

/**
 * An Illustrated picture's day version: `…/public/content-studio/illustrated/
 * <deck>/<card>.light.webp`, or its small copy under `illustrated/thumbs/`.
 */
const ILLUSTRATED_DAY_PICTURE = /^(.*\/public\/content-studio\/illustrated\/.+)\.light\.webp$/;

/**
 * The night twin of an Illustrated day picture, or null for any other picture.
 * Each Illustrated `.light.webp` has a `.dark.webp` beside it (the manifest
 * lists both), so the course stores one path and the theme picks the twin
 * (spec 2026-10-07 global deck catalog, §WP5 / Q4).
 */
export function illustratedNightTwin(src: string | null | undefined): string | null {
  const match = src ? ILLUSTRATED_DAY_PICTURE.exec(src) : null;
  return match ? `${match[1]}.dark.webp` : null;
}

export function CourseThumbnail({
  src,
  courseName,
  className,
  alt,
  showIcon = true,
}: CourseThumbnailProps) {
  // Two-tier fallback chain (2026-05-14):
  //   Tier 1 — per-course `src` (when admin uploaded / BE auto-set on create)
  //   Tier 2 — canonical DEFAULT_THUMBNAIL_URL (legacy courses with NULL src
  //            or 404 on Tier 1)
  //   Tier 3 — gradient + letter avatar (Tier 2 also 404s, e.g. fresh dev
  //            stack without seeded assets)
  //
  // `imageTier` tracks where we currently are. `errored` is rolled into
  // the state machine so a Tier-1 `onError` advances to Tier 2, and a
  // Tier-2 `onError` advances to Tier 3.
  const tier1 = src ? resolveAssetUrl(src) : '';
  const tier2 = resolveAssetUrl(DEFAULT_THUMBNAIL_URL);
  const [imageTier, setImageTier] = React.useState<1 | 2 | 3>(tier1 ? 1 : 2);
  // An Illustrated picture's night twin, shown in the dark theme. When it
  // fails to load, the day picture shows in both themes instead; it is not a
  // reason to give up the course's own picture.
  const nightTwin = illustratedNightTwin(src);
  const tier1Night = nightTwin ? resolveAssetUrl(nightTwin) : '';
  const [nightFailed, setNightFailed] = React.useState(false);

  // Reset back to the highest tier if src changes (e.g. admin picks a new
  // gallery thumbnail, or the page re-renders with a different course).
  React.useEffect(() => {
    setImageTier(tier1 ? 1 : 2);
    setNightFailed(false);
  }, [tier1]);

  const gradient = gradientFor(courseName);
  const letter = firstLetter(courseName);

  // Pick the URL for the current tier. Tier 3 has no URL — renders the
  // gradient/letter SVG block.
  const currentSrc =
    imageTier === 1 ? tier1 : imageTier === 2 ? tier2 : '';
  const themed = imageTier === 1 && tier1Night !== '' && !nightFailed;

  return (
    <div className={cn('relative w-full h-full overflow-hidden bg-muted', className)}>
      {themed ? (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            key="day"
            src={tier1}
            alt={alt ?? courseName}
            className="w-full h-full object-cover dark:hidden"
            onError={() => setImageTier(2)}
          />
          {/* The same picture at night: named once, by the day picture. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            key="night"
            src={tier1Night}
            alt=""
            className="hidden w-full h-full object-cover dark:block"
            onError={() => setNightFailed(true)}
          />
        </>
      ) : imageTier !== 3 ? (
        /* eslint-disable-next-line @next/next/no-img-element */
        <img
          key={imageTier}        // force re-mount so onError fires for the new URL
          src={currentSrc}
          alt={alt ?? courseName}
          className="w-full h-full object-cover"
          onError={() =>
            // Promote: T1 → T2 → T3. T3 is terminal.
            setImageTier((t) => (t === 1 ? 2 : 3))
          }
        />
      ) : (
        <div
          aria-label={alt ?? courseName}
          role="img"
          className={cn(
            'w-full h-full flex items-center justify-center bg-gradient-to-br',
            gradient,
          )}
        >
          {showIcon ? (
            <div className="flex flex-col items-center gap-1 text-foreground/60">
              <BookOpen className="w-6 h-6" aria-hidden="true" />
              <span className="text-xl font-bold">{letter}</span>
            </div>
          ) : (
            <span className="text-3xl font-bold text-foreground/70">{letter}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default CourseThumbnail;
